import React, { useContext, useState, useEffect } from "react";
import { CardContent, CircularProgress, Tooltip, Typography, CardActions } from "@mui/material";
import { context } from "utils/Provider";
import {
  useDatasetsByCubeIdQuery,
  useDataFromTablesByDatasetIdLazyQuery,
  useDeleteDatasetsBySourceFileIdMutation,
} from "graphql/types/graphql";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import UniqueFilenames from "./filenames";
import { enqueueSnackbar } from "notistack";
import DatasetPreviewComponent from "./DatasetPreviewComponent";
import pxToRem from "assets/theme/functions/pxToRem";
import SearchIcon from "@mui/icons-material/Search";
import DeleteDatasetButton from "./DeleteDatasetButton";
import { styled } from "@mui/system";
import colors from "assets/theme-dark/base/colors";

interface KeyValuePair {
  column: string;
  value?: string | null;
}

interface DynamicDataResponse {
  datasetId: string;
  cubeId: string;
  data: KeyValuePair[];
  originalFileName?: string;
}

interface DatasetsCardProps {
  setDatasetId: (id: string | null) => void;
  selectedDatasetId: string | null;
  dropzone?: JSX.Element;
  importButton?: JSX.Element;
  disableDeleteButton: boolean;
}

const DatasetsStyledCard = styled(MDBox)`
  width: 100%;
  background: ${colors.background.default};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid ${colors.grey[300]};
  margin-bottom: 1.5rem;
`;

const PreviewStyledCard = styled(MDBox)`
  width: 100%;
  background: ${colors.background.default};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid ${colors.grey[300]};
`;

const DatasetsCard: React.FC<DatasetsCardProps> = ({
  setDatasetId,
  selectedDatasetId,
  dropzone,
  importButton,
  disableDeleteButton,
}) => {
  const { selectedCube, setIsLoading } = useContext(context);
  const { loading, error, data } = useDatasetsByCubeIdQuery({
    variables: { cubeId: selectedCube?.cubeId, includeAlreadyMerged: false },
  });

  const [datasets, setDatasets] = useState(data?.datasetsByCubeId || []);
  const [searchQuery, setSearchQuery] = useState("");

  const [getDataFromTables, { loading: dataLoading, error: dataError, data: tableData }] =
    useDataFromTablesByDatasetIdLazyQuery({ notifyOnNetworkStatusChange: true });

  const [deleteBySourceFileId] = useDeleteDatasetsBySourceFileIdMutation();

  useEffect(() => {
    setDatasets(data?.datasetsByCubeId || []);
  }, [data]);

  if (loading) return <CircularProgress />;

  const handleCardClick = (datasetId: string) => {
    if (selectedDatasetId === datasetId) {
      setDatasetId(null);
    } else {
      setDatasetId(datasetId);
      getDataFromTables({ variables: { cubeName: selectedCube.name, datasetId, includeAlreadyMerged: false } });
    }
  };

  const handleDeleteDataset = (datasetId: string) => {
    setDatasets(datasets.filter((dataset) => dataset.id !== datasetId));
    setDatasetId(null);
  };

  const handleDeleteFile = async (sourceFileID: string) => {
    if (!selectedDatasetId) {
      return;
    }
    setIsLoading(true);

    try {
      const { data } = await deleteBySourceFileId({
        variables: {
          sourceFileId: sourceFileID,
          datasetId: selectedDatasetId,
          cubeName: selectedCube.name,
        },
        update: (cache) => {
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "dataFromTablesByDatasetId",
          });
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "datasetPreview",
          });
        },
      });

      if (data?.deleteDatasetsBySourceFileID.success) {
        enqueueSnackbar(`File with sourceFileID: ${sourceFileID} deleted successfully.`, { variant: "success" });
      } else {
        enqueueSnackbar(`Failed to delete file with sourceFileID: ${sourceFileID}`, { variant: "error" });
      }
    } catch (err) {
      enqueueSnackbar(`"Error deleting file:", ${err}`, { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const filteredColumnNames = ["DATASETID", "SOURCEFILEID", "CUBEID", "originalFileName", "datasetId", "cubeId"];

  const getUniqueColumns = (data: DynamicDataResponse[]) => {
    const columns = new Set<string>();
    data.forEach((row) => {
      row.data.forEach((col) => {
        if (!filteredColumnNames.includes(col.column)) {
          columns.add(col.column);
        }
      });
    });
    return Array.from(columns);
  };

  const uniqueFilenames = tableData && selectedDatasetId ? tableData.dataFromTablesByDatasetId.uniqueFilenames : [];
  const datasetName = datasets?.find((dataset) => dataset.id === selectedDatasetId)?.name || "Unknown name";

  const filteredDatasets = datasets.filter((dataset) => dataset.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const datasetMessage = "Are you sure you want to delete the dataset and its data from the database?";

  return (
    <MDBox sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
      <DatasetsStyledCard>
        <MDBox sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <MDTypography variant="h4" sx={{ color: colors.text.main, mb: 1, ml: "1rem" }}>
            Datasets
          </MDTypography>
        </MDBox>
        <MDBox
          sx={{
            display: "flex",
            alignItems: "center",
            width: "20%",
            marginLeft: "1rem",
          }}
        >
          <MDInput
            corrosive
            fullWidth
            placeholder="Search datasets..."
            value={searchQuery}
            onChange={(e: { target: { value: React.SetStateAction<string> } }) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: colors.text.secondary }} />,
            }}
            sx={{
              "& .MuiInputBase-root": {
                "& fieldset": { borderWidth: "0.5px", borderColor: colors.text.main },
                "&:hover fieldset": { borderColor: colors.primary.main },
                "&.Mui-focused fieldset": { borderColor: colors.primary.main },
                color: colors.text.main,
              },
              "& .MuiInputBase-input": {
                color: "#1A1A1A",
                height: pxToRem(23),
                py: pxToRem(8.5),
              },
              "& .MuiInputBase-root.MuiOutlinedInput-root ::placeholder": {
                color: "#1A1A1A",
              },
            }}
          />
        </MDBox>
        <MDBox sx={{ display: "flex", flexDirection: "row", overflowY: "auto", maxHeight: pxToRem(300) }}>
          <MDBox
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: "0.5rem",
              width: "100%",
              marginTop: "1rem",
              justifyContent: "flex-start",
              padding: "0.6rem",
            }}
          >
            {filteredDatasets.length > 0 ? (
              filteredDatasets.map((dataset) => (
                <MDBox
                  key={dataset.id}
                  sx={{
                    display: "flex",
                    padding: 0,
                    justifyContent: "center",
                    alignItems: "center",
                    position: "relative",
                    color: "secondary.main",
                    height: pxToRem(75),
                    width: pxToRem(150),
                    minWidth: pxToRem(150),
                    borderRadius: pxToRem(12),
                    userSelect: "none",
                    marginBottom: "0.2rem",
                    background:
                      selectedDatasetId === dataset.id
                        ? "linear-gradient(135deg, #0d00ff 0%, #b3c6ff 100%)"
                        : "linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(245, 245, 245, 0.7) 100%)",
                    backdropFilter: "blur(8px)",
                    boxShadow:
                      selectedDatasetId === dataset.id
                        ? "0 8px 24px rgba(13, 0, 255, 0.4), 0 4px 12px rgba(0, 0, 0, 0.2)"
                        : "0 6px 20px rgba(0, 0, 0, 0.15), 0 2px 6px rgba(0, 0, 0, 0.1), inset 0 1px 2px rgba(255, 255, 255, 0.3)",
                    border:
                      selectedDatasetId === dataset.id
                        ? "1px solid rgba(255, 255, 255, 0.5)"
                        : "1px solid rgba(0, 0, 0, 0.1)",
                    opacity: 1,
                    cursor: "pointer",
                    transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out, background 0.3s ease-in-out",
                    "&:hover": {
                      transform: "scale(1.05) translateY(-4px)",
                      boxShadow: "0 12px 32px rgba(0, 0, 0, 0.25), 0 6px 12px rgba(0, 0, 0, 0.15)",
                      background:
                        selectedDatasetId === dataset.id
                          ? "linear-gradient(135deg, #3b33ff 0%, #b3c6ff 100%)"
                          : "linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(250, 250, 250, 0.9) 100%)",
                    },
                    "&:active": {
                      transform: "scale(0.98)",
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                    },
                  }}
                  onClick={() => handleCardClick(dataset.id)}
                >
                  <CardContent
                    sx={{
                      padding: 0,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      width: "100%",
                    }}
                  >
                    {dataset.name.length >= 10 ? (
                      <Tooltip title={dataset.name} arrow>
                        <MDTypography
                          variant="button"
                          fontWeight="medium"
                          sx={{
                            color: selectedDatasetId === dataset.id ? "white.main" : "primary.main",
                          }}
                        >
                          {`${dataset.name.slice(0, 10)}...`}
                        </MDTypography>
                      </Tooltip>
                    ) : (
                      <MDTypography
                        variant="button"
                        fontWeight="medium"
                        sx={{
                          color: selectedDatasetId === dataset.id ? "white.main" : "primary.main",
                        }}
                      >
                        {dataset.name}
                      </MDTypography>
                    )}
                  </CardContent>
                  <CardActions
                    sx={{ display: "flex", justifyContent: "right", padding: 0 }}
                    disableSpacing
                    onClick={(e) => e.stopPropagation()}
                  >
                    {selectedDatasetId === dataset.id && (
                      <div style={{ right: 0, bottom: 0, position: "absolute" }}>
                        <DeleteDatasetButton
                          disableDeleteButton={disableDeleteButton}
                          datasetId={selectedDatasetId}
                          onDelete={() => handleDeleteDataset(selectedDatasetId)}
                          datasetName={datasetName}
                          confirmationMessage={datasetMessage}
                        />
                      </div>
                    )}
                  </CardActions>
                </MDBox>
              ))
            ) : (
              <MDBox sx={{ width: "100%", textAlign: "center", py: 2 }}>
                <MDTypography variant="body1" color="text.secondary">
                  No datasets found
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        </MDBox>
        {importButton && <MDBox sx={{ marginTop: "1.2rem", marginLeft: "1rem" }}>{importButton}</MDBox>}
      </DatasetsStyledCard>
      {selectedDatasetId && (
        <PreviewStyledCard>
          {dataLoading && <CircularProgress />}
          {dataError && <Typography color="error">Error loading table data: {dataError.message}</Typography>}
          {tableData && selectedDatasetId && (
            <MDBox
              sx={{
                display: "flex",
                gap: "1rem",
                minHeight: "50%",
                paddingTop: pxToRem(22),
                width: "100%",
                overflowX: "auto",
              }}
            >
              <MDBox sx={{ display: "flex", flexDirection: "column" }}>
                {dropzone && <MDBox sx={{ height: "50%" }}>{dropzone}</MDBox>}
                <UniqueFilenames filenames={uniqueFilenames} onDelete={handleDeleteFile} />
              </MDBox>
              <DatasetPreviewComponent
                cubeName={selectedCube.name}
                datasetId={selectedDatasetId}
                datasetName={datasetName}
                tableWidth="80%"
              />
            </MDBox>
          )}
        </PreviewStyledCard>
      )}
    </MDBox>
  );
};

export default DatasetsCard;
